// ─────────────────────────────────────────────────────────────────────────────
// GENERATED FILE — DO NOT EDIT HERE.
//
// Source of truth: invits-app-new/shared/authOptions.js
// Regenerate:     npm run sync:shared   (from invits-app-new)
//
// Editing this copy reintroduces exactly the app/web divergence that shared/
// exists to eliminate. Change the source, then re-run the sync.
// ─────────────────────────────────────────────────────────────────────────────

// shared/authOptions.js
//
// Which sign-in methods are offered, on the app and the website alike.
//
// Email and phone work with no outside setup (the auth-codes Edge Function
// sends both codes). Google and Apple need their providers configured in the
// Supabase dashboard first - with them off here, their buttons are simply not
// shown, so a release can never put a sign-in button in front of people that
// fails when tapped.
//
// ── Where ──────────────────────────────────────────────────────────────────
//
// Google on the WEBSITE only. That is where someone lands from a shared link
// wanting to RSVP or sign up in one tap; in the app, email and phone signup
// carry on as they are. No Apple: it is only required (App Store Guideline
// 4.8) for an iOS app that offers a third-party login, and the app offers
// none. To bring Google to the app later, it would need Apple alongside it on
// iOS - socialProviders already encodes that rule.

/** Google on the website. Needs the provider configured in Supabase first. */
export const GOOGLE_SIGN_IN_WEB = true;
/** Google in the apps (needs Apple too on iOS). Off. */
export const GOOGLE_SIGN_IN_APP = false;
export const APPLE_SIGN_IN_ENABLED = false;

/**
 * The social providers to show, in order.
 * @param {'ios'|'android'|'web'} platform
 * @returns {Array<'apple'|'google'>}
 */
export function socialProviders(platform) {
  if (platform === 'web') return GOOGLE_SIGN_IN_WEB ? ['google'] : [];
  const out = [];
  if (APPLE_SIGN_IN_ENABLED) out.push('apple');
  // Guideline 4.8: on iOS, Google only alongside Apple.
  if (GOOGLE_SIGN_IN_APP && (platform !== 'ios' || APPLE_SIGN_IN_ENABLED)) out.push('google');
  return out;
}

/**
 * Dial codes offered beside a number typed into the "email or phone" field.
 * The US and India first - that is where most people signing up are - then
 * the rest roughly by how often they appear. `national` is the length range
 * of a number without its country code or trunk 0, used to tell "98765 43210"
 * from "91 98765 43210".
 */
export const PHONE_COUNTRIES = [
  { iso2: 'US', code: '1', name: 'United States', flag: '🇺🇸', national: [10, 10] },
  { iso2: 'IN', code: '91', name: 'India', flag: '🇮🇳', national: [10, 10] },
  { iso2: 'CA', code: '1', name: 'Canada', flag: '🇨🇦', national: [10, 10] },
  { iso2: 'GB', code: '44', name: 'United Kingdom', flag: '🇬🇧', national: [10, 10] },
  { iso2: 'AU', code: '61', name: 'Australia', flag: '🇦🇺', national: [9, 9] },
  { iso2: 'AE', code: '971', name: 'United Arab Emirates', flag: '🇦🇪', national: [9, 9] },
  { iso2: 'SG', code: '65', name: 'Singapore', flag: '🇸🇬', national: [8, 8] },
  { iso2: 'NZ', code: '64', name: 'New Zealand', flag: '🇳🇿', national: [8, 10] },
  { iso2: 'IE', code: '353', name: 'Ireland', flag: '🇮🇪', national: [9, 9] },
  { iso2: 'DE', code: '49', name: 'Germany', flag: '🇩🇪', national: [10, 11] },
  { iso2: 'FR', code: '33', name: 'France', flag: '🇫🇷', national: [9, 9] },
  { iso2: 'NL', code: '31', name: 'Netherlands', flag: '🇳🇱', national: [9, 9] },
  { iso2: 'SA', code: '966', name: 'Saudi Arabia', flag: '🇸🇦', national: [9, 9] },
  { iso2: 'QA', code: '974', name: 'Qatar', flag: '🇶🇦', national: [8, 8] },
  { iso2: 'KW', code: '965', name: 'Kuwait', flag: '🇰🇼', national: [8, 8] },
  { iso2: 'OM', code: '968', name: 'Oman', flag: '🇴🇲', national: [8, 8] },
  { iso2: 'BH', code: '973', name: 'Bahrain', flag: '🇧🇭', national: [8, 8] },
  { iso2: 'MY', code: '60', name: 'Malaysia', flag: '🇲🇾', national: [9, 10] },
  { iso2: 'NP', code: '977', name: 'Nepal', flag: '🇳🇵', national: [10, 10] },
  { iso2: 'BD', code: '880', name: 'Bangladesh', flag: '🇧🇩', national: [10, 10] },
  { iso2: 'LK', code: '94', name: 'Sri Lanka', flag: '🇱🇰', national: [9, 9] },
  { iso2: 'PK', code: '92', name: 'Pakistan', flag: '🇵🇰', national: [10, 10] },
  { iso2: 'ZA', code: '27', name: 'South Africa', flag: '🇿🇦', national: [9, 9] },
  { iso2: 'HK', code: '852', name: 'Hong Kong', flag: '🇭🇰', national: [8, 8] },
  { iso2: 'JP', code: '81', name: 'Japan', flag: '🇯🇵', national: [10, 10] },
];

/**
 * A number being typed without its own "+": starts with a digit (or an
 * opening bracket) and is nothing but digits and separators so far. That is
 * when the field shows its country code picker. "+1 415..." carries its own code and needs none; an
 * email that happens to start with a digit stops matching at its first letter.
 */
export function looksLikeLocalPhone(raw) {
  // "(415) 555-0100" starts with a bracket and is as much a number as any.
  return /^\(?\d[\d\s\-()]*$/.test(String(raw || '').trim());
}

/**
 * What someone typed into a single "email or phone" field.
 *
 * Anything with an @ is an email. Digits (with spaces, dashes, brackets) are a
 * phone number: with a leading + it carries its own country code; without
 * one it is a number in the country `defaultCode` belongs to (the picker
 * beside the field). A leading trunk 0 is dropped ("098765 43210"), and a
 * number that already starts with the country code is not given it twice
 * ("91 98765 43210" with +91 picked).
 *
 * @param {string} raw
 * @param {string} [defaultCode]  dial code without the +, e.g. '1' or '91'
 * @returns {{kind: 'email', email: string} | {kind: 'phone', phone: string} | {kind: null}}
 */
export function parseIdentifier(raw, defaultCode = '91') {
  const value = String(raw || '').trim();
  if (!value) return { kind: null };
  if (value.includes('@')) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value)
      ? { kind: 'email', email: value.toLowerCase() }
      : { kind: null };
  }
  if (!/^\+?[\d\s\-()]{7,20}$/.test(value)) return { kind: null };

  const digits = value.replace(/\D/g, '');
  if (value.startsWith('+')) {
    // A trunk 0 kept after the code ("+91 098765 43210", "+44 (0)7911 123456")
    // is not part of the number; sent with it, the SMS is refused.
    const known = PHONE_COUNTRIES.find(({ code, national: [min, max] }) =>
      digits.startsWith(`${code}0`) && digits.length - code.length - 1 >= min && digits.length - code.length - 1 <= max);
    const phone = known ? `${known.code}${digits.slice(known.code.length + 1)}` : digits;
    return phone.length >= 8 && phone.length <= 15 ? { kind: 'phone', phone } : { kind: null };
  }

  const code = String(defaultCode || '').replace(/\D/g, '');
  const [min, max] = PHONE_COUNTRIES.find((c) => c.code === code)?.national ?? [10, 10];
  const national = digits.replace(/^0+/, '');
  const fits = (n) => n >= min && n <= max;

  // The country code typed without its +.
  if (code && national.startsWith(code) && fits(national.length - code.length)) {
    return { kind: 'phone', phone: national };
  }
  if (code && fits(national.length) && code.length + national.length <= 15) {
    return { kind: 'phone', phone: `${code}${national}` };
  }
  // Long enough to carry some other country's code, typed without its +.
  if (national.length >= 11 && national.length <= 15) return { kind: 'phone', phone: national };
  return { kind: null };
}

/**
 * A full number split into its dial code and the rest, e.g. '14155550100' ->
 * { code: '1', national: '4155550100' }. `preferredCode` (the picker's) is
 * tried first, then the longest code in PHONE_COUNTRIES that fits.
 */
export function splitPhone(phone, preferredCode) {
  const digits = String(phone || '').replace(/\D/g, '');
  const codes = [preferredCode, ...PHONE_COUNTRIES.map((c) => c.code).sort((a, b) => b.length - a.length)]
    .map((c) => String(c || '').replace(/\D/g, ''))
    .filter(Boolean);
  const code = codes.find((c) => digits.startsWith(c) && digits.length - c.length >= 6);
  return code ? { code, national: digits.slice(code.length) } : { code: null, national: digits };
}

/** '14155550100' -> '+1 4155550100': the code set apart, so a wrong country shows. */
export function formatPhone(phone, preferredCode) {
  const { code, national } = splitPhone(phone, preferredCode);
  return code ? `+${code} ${national}` : `+${national}`;
}

// IANA time zones of the PHONE_COUNTRIES, for phoneCountryForTimeZone.
// US zones are listed; Canada's few are named; other American zones (Mexico,
// Brazil, ...) are not +1 and are left out.
const US_ZONES = new Set([
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'America/Phoenix', 'America/Anchorage', 'America/Detroit', 'America/Boise',
  'America/Juneau', 'America/Sitka', 'America/Nome', 'America/Adak', 'America/Menominee',
  'America/Metlakatla', 'America/Yakutat', 'Pacific/Honolulu', 'America/Puerto_Rico',
]);
const CA_ZONES = new Set([
  'America/Toronto', 'America/Vancouver', 'America/Edmonton', 'America/Winnipeg',
  'America/Halifax', 'America/St_Johns', 'America/Regina', 'America/Montreal',
  'America/Moncton', 'America/Whitehorse', 'America/Yellowknife', 'America/Iqaluit',
]);
const ZONE_COUNTRY = {
  'Asia/Kolkata': 'IN', 'Asia/Calcutta': 'IN',
  'Europe/London': 'GB', 'Asia/Dubai': 'AE', 'Asia/Singapore': 'SG',
  'Pacific/Auckland': 'NZ', 'Europe/Dublin': 'IE', 'Europe/Berlin': 'DE',
  'Europe/Paris': 'FR', 'Europe/Amsterdam': 'NL', 'Asia/Riyadh': 'SA',
  'Asia/Qatar': 'QA', 'Asia/Kuwait': 'KW', 'Asia/Muscat': 'OM', 'Asia/Bahrain': 'BH',
  'Asia/Kuala_Lumpur': 'MY', 'Asia/Kathmandu': 'NP', 'Asia/Katmandu': 'NP',
  'Asia/Dhaka': 'BD', 'Asia/Colombo': 'LK', 'Asia/Karachi': 'PK',
  'Africa/Johannesburg': 'ZA', 'Asia/Hong_Kong': 'HK', 'Asia/Tokyo': 'JP',
};

/**
 * The PHONE_COUNTRIES entry for a device's time zone ('America/Chicago' ->
 * the US), or null when the zone is not one of theirs. The app's default
 * beside a phone number: offline, instant, and not moved by a VPN. Starting
 * every picker on India sent American numbers out as +91.
 */
export function phoneCountryForTimeZone(zone) {
  const z = String(zone || '');
  let iso2 = ZONE_COUNTRY[z] || null;
  if (!iso2 && (US_ZONES.has(z) || /^America\/(Indiana|Kentucky|North_Dakota)\//.test(z))) iso2 = 'US';
  if (!iso2 && CA_ZONES.has(z)) iso2 = 'CA';
  if (!iso2 && z.startsWith('Australia/')) iso2 = 'AU';
  return iso2 ? PHONE_COUNTRIES.find((c) => c.iso2 === iso2) || null : null;
}

/**
 * The PHONE_COUNTRIES entry for a country ('IN') or a dial code ('+44', '44');
 * null when it is not one of them. A shared dial code (+1) is its first entry.
 */
export function phoneCountryFor(value) {
  const v = String(value || '').trim().toUpperCase();
  if (!v) return null;
  if (/^[A-Z]{2}$/.test(v)) return PHONE_COUNTRIES.find((c) => c.iso2 === v) || null;
  const code = v.replace(/\D/g, '');
  return (code && PHONE_COUNTRIES.find((c) => c.code === code)) || null;
}
